import { ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogPortal,
} from '@/ui/alert-dialog';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { cn } from '@/lib/utils';

// 主色渐变样式
const primaryGradient = {
  background: 'linear-gradient(135deg, #7bb8ff 0%, #3F3663 100%)',
};

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
  variant?: 'default' | 'destructive';
  loading?: boolean;
  zIndex?: string;
  closeOnConfirm?: boolean;
  confirmButtonClassName?: string;
  confirmButtonStyle?: React.CSSProperties;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = '确认',
  cancelText = '取消',
  onConfirm,
  variant = 'default',
  loading = false,
  zIndex,
  closeOnConfirm = true,
  confirmButtonClassName,
  confirmButtonStyle,
}: ConfirmDialogProps) {
  const handleConfirm = async () => {
    try {
      await onConfirm();
      if (closeOnConfirm) {
        onOpenChange(false);
      }
    } catch (error) {
      // 错误处理由调用方负责
      console.error('确认操作失败:', error);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {zIndex ? (
        <AlertDialogPortal>
          <AlertDialogPrimitive.Overlay
            className={cn(
              'fixed inset-0 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              zIndex
            )}
          />
          <AlertDialogPrimitive.Content
            className={cn(
              'fixed left-[50%] top-[50%] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
              zIndex
            )}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>{title}</AlertDialogTitle>
              <AlertDialogDescription asChild>
                {typeof description === 'string' ? (
                  <span>{description}</span>
                ) : (
                  description
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={loading}>
                {cancelText}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirm}
                disabled={loading}
                className={cn(
                  variant === 'destructive'
                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-600'
                    : 'border-0',
                  confirmButtonClassName
                )}
                style={
                  confirmButtonStyle ??
                  (variant === 'destructive' ? undefined : primaryGradient)
                }
              >
                {loading ? '处理中...' : confirmText}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogPrimitive.Content>
        </AlertDialogPortal>
      ) : (
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              {typeof description === 'string' ? (
                <span>{description}</span>
              ) : (
                description
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>
              {cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={loading}
              className={
                variant === 'destructive'
                  ? 'bg-red-600 hover:bg-red-700 focus:ring-red-600'
                  : 'border-0'
              }
              style={variant === 'destructive' ? undefined : primaryGradient}
            >
              {loading ? '处理中...' : confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      )}
    </AlertDialog>
  );
}
